import { formatLocalDate } from '../utils/localDateTime';
import React, { useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import type { Organization, UserProfile } from '@types';
import { db, functions } from '../../firebaseConfig';
import { getOrganizationDisplayName } from '@common/organizationDisplayName';
import { canUseServiceRequests } from '../common/clientAccessPolicy';
import { serviceRequestDownloadUrl, type ServiceRequestFile } from '../common/serviceRequestFiles';
import { clientDashboardCountries, primaryRequestCountry } from '../utils/clientDashboardCountries';
import { GlobalLiveNewsGrid } from './GlobalLiveNewsGrid';
import DailyCountryBriefs from './DailyCountryBriefs';
import CountryFacilitiesCard from './CountryFacilitiesCard';
import { RiskMonitorDashboard } from './RiskMonitorDashboard';
import useGoogleMaps from './useGoogleMaps';
import 'flag-icons/css/flag-icons.min.css';

type RequestSummary = {
    id: string;
    requestReference: string;
    serviceName: string;
    serviceCode?: string;
    status: string;
    submittedAt?: string;
    focusRegionCountry?: string;
    dailyMicroCountry?: string;
    deliveryReports?: Array<{ id: string; title: string }>;
    deliveryFiles?: ServiceRequestFile[];
    deliveryDashboard?: { projectId: string; title?: string };
};

function requestDate(value?: string): string {
    if (!value)
        return 'Draft';
    return formatLocalDate(value, 'Date unavailable');
}

const CountryDashboard: React.FC<{
    organization: Organization;
    currentUser: UserProfile;
    iso3: string;
    riskData?: any[];
    readOnly: boolean;
    onNavigate: (nav: { page: string; params?: any }) => void;
}> = ({ organization, currentUser, iso3, riskData, readOnly, onNavigate }) => {
    const [requests, setRequests] = useState<RequestSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [retry, setRetry] = useState(0);
    const [openingFile, setOpeningFile] = useState('');
    const [fileError, setFileError] = useState('');
    const { isLoaded } = useGoogleMaps();
    const selectedIso3 = iso3.trim().toUpperCase();

    useEffect(() => {
        let active = true;
        setLoading(true);
        setError('');
        setRequests([]);
        const callable = httpsCallable(functions, 'manageServiceRequests');
        void callable({ action: 'summary', organizationId: organization.id }).then(result => {
            if (active)
                setRequests(((result.data as any).requests || []) as RequestSummary[]);
        }).catch(() => {
            if (active)
                setError('Country service requests could not load.');
        }).finally(() => {
            if (active)
                setLoading(false);
        });
        return () => { active = false; };
    }, [organization.id, retry]);

    const deliveredCountries = useMemo(() => requests.filter(request =>
        request.serviceCode === 'S-07' && request.dailyMicroCountry &&
        ['delivered', 'invoiced', 'completed'].includes(request.status)
    ).map(request => request.dailyMicroCountry!), [requests]);
    const country = clientDashboardCountries(organization, deliveredCountries).find(item => item.iso3 === selectedIso3);
    const countryRequests = useMemo(() => requests.filter(request =>
        primaryRequestCountry(request.focusRegionCountry || request.dailyMicroCountry)?.iso3 === selectedIso3
    ), [requests, selectedIso3]);
    const scopedRiskData = useMemo(() => (riskData || []).filter(item =>
        primaryRequestCountry(item.iso3 || item.countryCode || item.country)?.iso3 === selectedIso3
    ), [riskData, selectedIso3]);
    const canOpenRequests = canUseServiceRequests(currentUser.role, currentUser.serviceRequestAccess);
    const openFile = async (requestId: string, file: ServiceRequestFile) => {
        const popup = window.open('about:blank', '_blank');
        if (popup)
            popup.opener = null;
        setOpeningFile(file.path);
        setFileError('');
        try {
            const url = await serviceRequestDownloadUrl(organization.id, requestId, file);
            if (popup)
                popup.location.href = url;
            else
                window.location.assign(url);
        } catch {
            popup?.close();
            setFileError('Could not open the delivered file.');
        } finally {
            setOpeningFile('');
        }
    };

    if (loading)
        return <div role="status" className="rounded-2xl border border-slate-200 bg-white p-10 text-sm text-slate-600">Loading country dashboard…</div>;

    if (error && !country)
        return <div role="alert" className="rounded-2xl border border-red-200 bg-white p-8 text-sm text-red-700">
            {error} <button type="button" onClick={() => setRetry(value => value + 1)} className="font-bold underline">Retry</button>
        </div>;

    if (!country)
        return <div className="rounded-2xl border border-slate-200 bg-white p-8">
            <h1 className="text-xl font-bold text-slate-900">Country dashboard unavailable</h1>
            <p className="mt-2 text-sm text-slate-600">This country is outside your configured locations and delivered reports.</p>
            <button type="button" onClick={() => onNavigate({ page: 'organizationDashboard' })} className="mt-5 font-bold text-blue-700 hover:underline">Back to Global/Home dashboard</button>
        </div>;

    return <div className="mx-auto w-full max-w-[2000px] space-y-8">
        <header className="rounded-2xl border border-slate-700 bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950 p-8 text-white shadow-xl sm:p-12">
            <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0">
                    <button type="button" onClick={() => onNavigate({ page: 'organizationDashboard' })}
                        className="mb-8 rounded-lg border border-white/20 px-4 py-2 text-sm font-bold text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white">
                        ← Global/Home dashboard
                    </button>
                    <p className="text-xs font-bold uppercase tracking-widest text-indigo-300">{getOrganizationDisplayName(organization)} · Country Dashboard</p>
                    <h1 className="mt-3 flex items-center gap-5 text-4xl font-extrabold tracking-tight md:text-5xl">
                        <span className={`fi fi-${country.iso2.toLowerCase()} shrink-0 rounded shadow`} role="img" aria-label={`${country.name} flag`} />
                        {country.name}
                    </h1>
                    <p className="mt-4 max-w-2xl text-slate-300">Service work, intelligence briefs, live feeds, and risk events for {country.name}.</p>
                </div>
                <div className="w-full lg:w-[360px] lg:shrink-0">
                    <DailyCountryBriefs organization={organization} deliveredCountries={deliveredCountries} countryIso3={selectedIso3} placement="header"
                        onOpen={(reportData, name) => onNavigate({ page: 'CountryAnalysisResults', params: { reportData, country: name, isDailyMicro: true } })} />
                </div>
            </div>
        </header>

        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" aria-label={`${country.name} service requests`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div><h2 className="text-xl font-bold text-slate-900">Service Requests</h2><p className="mt-1 text-sm text-slate-600">Requests focused on {country.name}, with linked reports and dashboards.</p></div>
                {canOpenRequests && <button type="button" onClick={() => onNavigate({ page: 'serviceRequests' })} className="text-sm font-bold text-blue-700 hover:underline">Manage requests</button>}
            </div>
            {error && <p role="alert" className="mt-4 text-sm text-red-700">{error} <button type="button" onClick={() => setRetry(value => value + 1)} className="underline">Retry</button></p>}
            {fileError && <p role="alert" className="mt-4 text-sm text-red-700">{fileError}</p>}
            {countryRequests.length ? <div className="mt-5 grid gap-4 lg:grid-cols-2">
                {countryRequests.map(request => <article key={request.id} className="rounded-xl border border-slate-200 bg-slate-50 p-5">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div><p className="text-xs font-bold uppercase tracking-wider text-blue-700">{request.requestReference}</p><h3 className="mt-1 font-bold text-slate-900">{request.serviceName}</h3></div>
                        <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-bold capitalize text-blue-800">{request.status.replaceAll('_', ' ')}</span>
                    </div>
                    <p className="mt-2 text-xs text-slate-500">Submitted {requestDate(request.submittedAt)}</p>
                    <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-sm font-bold">
                        {canOpenRequests && <button type="button" onClick={() => onNavigate({ page: 'serviceRequests', params: { serviceRequestId: request.id } })} className="text-blue-700 hover:underline">Open request</button>}
                        {request.deliveryDashboard?.projectId && <button type="button" onClick={() => onNavigate({ page: 'srm_command_center', params: { projectId: request.deliveryDashboard!.projectId, view: 'dashboard' } })} className="text-emerald-700 hover:underline">{request.deliveryDashboard.title || 'Open dashboard'}</button>}
                        {request.deliveryReports?.map(report => <button key={report.id} type="button" onClick={() => onNavigate({ page: 'analysisDisplay', params: { reportId: report.id } })} className="text-blue-700 hover:underline">{report.title}</button>)}
                        {request.deliveryFiles?.map(file => <button key={file.path} type="button" disabled={openingFile === file.path} onClick={() => void openFile(request.id, file)} className="text-blue-700 hover:underline disabled:opacity-50">{openingFile === file.path ? 'Opening…' : file.name}</button>)}
                    </div>
                </article>)}
            </div> : !error && <p className="mt-5 text-sm text-slate-500">No service requests focus on {country.name} yet.</p>}
        </section>

        <CountryFacilitiesCard organization={organization} iso3={selectedIso3} countryName={country.name} currentUser={currentUser} readOnly={readOnly} onNavigate={onNavigate} />

        <section className="rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-xl">
            <h2 className="text-xl font-bold text-white">ECC Global Command Center · {country.name}</h2>
            <p className="mb-6 mt-1 text-xs font-medium uppercase tracking-widest text-slate-400">Country live feeds</p>
            <GlobalLiveNewsGrid key={selectedIso3} layout="horizontal" maxFeeds={8} lockedCountry={country.name} />
        </section>

        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" aria-label={`${country.name} risk map`}>
            <div className="h-[800px]">
                <RiskMonitorDashboard db={db} currentUser={currentUser} currentOrganization={organization} isLoaded={isLoaded}
                    riskData={scopedRiskData} riskEvents={[]} filterIso3={selectedIso3} countryName={country.name} lockCountry
                    allCountryPins choroplethEnabled onNavigate={onNavigate} readOnly={readOnly} />
            </div>
        </section>
    </div>;
};

export default CountryDashboard;
